import React, { useState, useEffect, useMemo } from "react";
import { Platform } from "react-native";
import {
  View,
  StyleSheet,
  ActivityIndicator,
  useWindowDimensions,
  ScrollView,
  TouchableOpacity,
  Text,
  Modal,
} from "react-native";
import {
  NormalText,
  TitleText,
  PrimaryButton,
  SecondaryButton,
  RainbowBorder,
  StickyOnboardingModal,
  ToastMessage,
  beforeAfterSteps,
} from "../../../../common/native";
import {
  assignPatientToSideBySideBatch,
  TPostSideBySideUnsortedBatchResponse,
  TPatientInfo,
  TSideBySidePair,
  TSideBySideProcessedImage,
} from "../../../../../services/api-clinic";
import { logUIError } from "../../../../../services/api-misc";
import { TTemplate } from "../../../../../utils/types";
import TemplateCarousel from "./template-carousel/TemplateCarousel";
import DrawAllImages from "./DrawAllImages";
import Download from "../../../../../assets/icons/Download.icon";
import PersonAdd from "../../../../../assets/icons/PersonAdd.icon";
import Save from "../../../../../assets/icons/Save.icon";
import { SessionContext } from "../../../../../contexts";
import { useOperationQueue, OperationType } from "./hooks/useOperationQueue";
import { TTemplateData, useTemplateData } from "./hooks/useTemplateData";
import { useOnboardingTips } from "../../../../../contexts/OnboardingTipsContext";
import { TOOLTIP_STEPS } from "../../../../../utils/constant";
import { mixpanel } from "../../../../../utils/mixpanel";
import TemplateSettingsModal from "../template/TemplateSettingsModal";
import ManualPairingPanel from "./ManualPairingPanel";
import AssignImageToPatientModal from "../../../../uncommon/native/modals/AssignImageToPatientModal";

export type TBeforeAfterWorkflowState = {
  templatesData: TTemplateData[];
  unpairedBeforeImages: TSideBySideProcessedImage[];
  unpairedAfterImages: TSideBySideProcessedImage[];
  activeSlideIndex: number;
  sourceTemplateSignature: string;
};

type Step4Props = {
  loading: boolean;
  error: string | null;
  templateEdit: TTemplate;
  beforeAfterResponse: TPostSideBySideUnsortedBatchResponse | null;
  uploadedImages: {
    preOp: Blob[];
    postOp: Blob[];
  };
  processedImageUrls: {
    preOp: string[];
    postOp: string[];
  };
  onStartAgainFresh: () => void;
  onStartAgainWithImages: () => void;
  preservedWorkflowState?: TBeforeAfterWorkflowState;
  onWorkflowStateChange?: (state: TBeforeAfterWorkflowState) => void;
  tipsEnabled?: boolean;
};

const Step4: React.FC<Step4Props> = ({
  error,
  loading,
  templateEdit,
  beforeAfterResponse,
  uploadedImages,
  processedImageUrls,
  onStartAgainFresh,
  onStartAgainWithImages,
  preservedWorkflowState,
  onWorkflowStateChange,
  tipsEnabled = false,
}) => {
  const { width } = useWindowDimensions();
  const { height } = useWindowDimensions();
  const isSmallScreenDesktop = height < 800 && Platform.OS === "web";
  const session = React.useContext(SessionContext)?.session;
  const { tipsState, markStepComplete } = useOnboardingTips();
  const wasWorkflowRestored = React.useRef(
    preservedWorkflowState !== undefined,
  ).current;
  const templateEditSignature = JSON.stringify(templateEdit);

  // Store the cropped image data URLs
  const {
    templatesData,
    setTemplatesData,
    drawAllImages,
    setDrawAllImages,
    updateBeforeCroppedImage,
    updateAfterCroppedImage,
    updateBothCroppedImages,
    appendTemplatePair,
    isRebasingTemplates,
  } = useTemplateData({
    templateEdit,
    beforeAfterResponse,
    width,
    initialTemplatesData: preservedWorkflowState?.templatesData,
    initialSourceTemplateSignature:
      preservedWorkflowState?.sourceTemplateSignature,
  });

  const [activeSlideIndex, setActiveSlideIndex] = useState<number>(
    preservedWorkflowState?.activeSlideIndex ?? 0,
  );
  const [currentDownloadIndex, setCurrentDownloadIndex] = useState<number>(0);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("✅ Images saved");
  const [hasAutoSavedInitialRender, setHasAutoSavedInitialRender] =
    useState(false);
  const [showStartAgainModal, setShowStartAgainModal] = useState(false);
  const [showAssignPatientModal, setShowAssignPatientModal] = useState(false);
  const [showUnpairedModal, setShowUnpairedModal] = useState(false);
  const [unpairedBeforeImages, setUnpairedBeforeImages] = useState<
    TSideBySideProcessedImage[]
  >(() => preservedWorkflowState?.unpairedBeforeImages ?? []);
  const [unpairedAfterImages, setUnpairedAfterImages] = useState<
    TSideBySideProcessedImage[]
  >(() => preservedWorkflowState?.unpairedAfterImages ?? []);
  const {
    currentOperation,
    queueOperation,
    handleImageGenerated,
    handleOperationError,
  } = useOperationQueue({
    templatesData,
    setDrawAllImages,
    setActiveSlideIndex,
    setCurrentDownloadIndex,
    setToastMessage,
    setShowToast,
  });

  useEffect(() => {
    if (!hasAutoSavedInitialRender && templatesData.length > 0) {
      queueOperation("saving");
      setHasAutoSavedInitialRender(true);
    }
  }, [hasAutoSavedInitialRender, queueOperation, templatesData.length]);

  useEffect(() => {
    if (preservedWorkflowState !== undefined) return;
    if (!beforeAfterResponse) {
      setUnpairedBeforeImages([]);
      setUnpairedAfterImages([]);
      return;
    }

    setUnpairedBeforeImages(beforeAfterResponse.unpairedBeforeImages || []);
    setUnpairedAfterImages(beforeAfterResponse.unpairedAfterImages || []);
  }, [beforeAfterResponse, preservedWorkflowState]);

  useEffect(() => {
    if (!onWorkflowStateChange || isRebasingTemplates) return;
    const isReady =
      wasWorkflowRestored ||
      beforeAfterResponse?.allPairs.length === 0 ||
      templatesData.length > 0;
    if (!isReady) return;

    onWorkflowStateChange({
      templatesData,
      unpairedBeforeImages,
      unpairedAfterImages,
      activeSlideIndex,
      sourceTemplateSignature: templateEditSignature,
    });
  }, [
    activeSlideIndex,
    beforeAfterResponse,
    isRebasingTemplates,
    onWorkflowStateChange,
    templateEditSignature,
    templatesData,
    unpairedAfterImages,
    unpairedBeforeImages,
    wasWorkflowRestored,
  ]);

  const beforeImageOptions = useMemo(() => {
    if (!beforeAfterResponse) return [];
    const allBefore = [
      ...beforeAfterResponse.allPairs.map((pair) => pair.before),
      ...beforeAfterResponse.unpairedBeforeImages,
    ];
    const byId = new Map<number, TSideBySideProcessedImage>();
    allBefore.forEach((image) => {
      byId.set(image.id_in_database, image);
    });
    return Array.from(byId.values());
  }, [beforeAfterResponse]);

  const afterImageOptions = useMemo(() => {
    if (!beforeAfterResponse) return [];
    const allAfter = [
      ...beforeAfterResponse.allPairs.map((pair) => pair.after),
      ...beforeAfterResponse.unpairedAfterImages,
    ];
    const byId = new Map<number, TSideBySideProcessedImage>();
    allAfter.forEach((image) => {
      byId.set(image.id_in_database, image);
    });
    return Array.from(byId.values());
  }, [beforeAfterResponse]);

  const handleSetTemplatesDataTemplate = (
    updaterFunction: (prev: TTemplate) => TTemplate,
    index: number,
  ) => {
    setTemplatesData((prevData) => {
      const currentTemplate = prevData[index].template;
      const newTemplate = updaterFunction(currentTemplate);
      // console.log("🛠️ Updating template at index", index, { newTemplate });

      return prevData.map((item, i) =>
        i === index ? { ...item, template: newTemplate } : item,
      );
    });
  };

  const handleClickSaveChanges = () => {
    if (templatesData.length === 0) {
      setToastMessage("⚠️ No images to save");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
      return;
    }

    // Queue a save operation
    queueOperation("saving");
  };

  const handleDownloadImage = () => {
    if (templatesData.length === 0) {
      setToastMessage("⚠️ No images to download");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
      if (tipsState.downloadImages && !tipsState.beforeAfterTipsCompleted) {
        markStepComplete(TOOLTIP_STEPS.DOWNLOAD_IMAGES);
      }
      return;
    }

    // Queue a download operation
    queueOperation("downloading");
  };

  const handleDownloadAllImages = () => {
    if (templatesData.length === 0) {
      setToastMessage("⚠️ No images to download");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
      if (tipsState.downloadImages && !tipsState.beforeAfterTipsCompleted) {
        markStepComplete(TOOLTIP_STEPS.DOWNLOAD_IMAGES);
      }
      return;
    }

    if (templatesData.length <= 1) {
      // If there's only one image, just do a regular download
      queueOperation("downloading");
      console.log("Downloading single image");
    } else {
      // Otherwise queue a download all operation
      queueOperation("downloadingAll");
      console.log("Downloading all images");
    }
  };

  const handleExportRenderError = React.useCallback(
    (message: string) => {
      handleOperationError(message);

      logUIError({
        message: "Before-after export render failed",
        page_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        user_email: session?.userEmail || undefined,
        clinic_name: session?.clinicName || undefined,
        clinic_id: session?.clinicId || undefined,
        stack_trace: message,
        context: {
          component: "Step4",
          flow: "BeforeAfter",
          operation: currentOperation,
          activeSlideIndex,
          templateCount: templatesData.length,
          uiMessage: message,
        },
      }).catch((logErr) =>
        console.warn("logUIError failed in BeforeAfter Step4 export", logErr),
      );
    },
    [
      session,
      currentOperation,
      activeSlideIndex,
      templatesData.length,
      handleOperationError,
    ],
  );

  if (loading)
    return (
      <ActivityIndicator
        size="large"
        color="#00b7f4"
        style={{ marginTop: 50, marginHorizontal: "auto" }}
      />
    );

  if (error) return <NormalText text={error} style={{ color: "red" }} />;

  const handleManualPair = async (
    beforeImage: TSideBySideProcessedImage,
    afterImage: TSideBySideProcessedImage,
  ) => {
    const manualPair: TSideBySidePair = {
      before: beforeImage,
      after: afterImage,
      concat: {
        put_url: "",
        s3_key: "",
        thumbnail_s3_key: "",
        filename: `manual-pair-${Date.now()}.png`,
        id_in_database: -(Date.now() + Math.floor(Math.random() * 1000)),
      },
    };

    await appendTemplatePair(manualPair);

    setUnpairedBeforeImages((prev) =>
      prev.filter(
        (image) => image.id_in_database !== beforeImage.id_in_database,
      ),
    );
    setUnpairedAfterImages((prev) =>
      prev.filter(
        (image) => image.id_in_database !== afterImage.id_in_database,
      ),
    );

    setToastMessage("✅ Manual pair added");
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  const handleAssignToPatient = async (patient: TPatientInfo) => {
    if (!session) return;
    const batchUuid = beforeAfterResponse?.batchUuid;
    if (!batchUuid) {
      setToastMessage("⚠️ Missing batch id for assignment");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
      return;
    }

    try {
      mixpanel.track("Assign Image To Patient Triggered", {
        "Clinic Name": session?.clinicName ?? "clinic not found",
        Flow: "BeforeAfter",
        Endpoint:
          "/clinics/v1/side-by-side/{clinic_id}/before-after/batch/{batch_uuid}/assign-patient",
        "Batch UUID": batchUuid,
        "Patient UUID": patient.uuid,
      });
      await assignPatientToSideBySideBatch(session, batchUuid, patient.uuid);
      setToastMessage(
        `Assigned image to ${patient.firstName} ${patient.lastName}`,
      );
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
    } catch (error) {
      setToastMessage("⚠️ Failed to assign image. Please try again.");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
      throw error;
    }
  };

  return (
    <>
      <ScrollView contentContainerStyle={styles.container1}>
        <View style={styles.container}>
          {templatesData.length === 0 && (
            <View style={styles.noAutoPairAlert}>
              <NormalText
                text="⚠️ No automatic pairs were found. Open Unpaired to pair manually."
                style={styles.noAutoPairAlertText}
              />
              <PrimaryButton
                text="Open Unpaired"
                width={180}
                roundBorderRadius
                onPress={() => setShowUnpairedModal(true)}
              />
            </View>
          )}

          <View style={styles.carouselWrapper}>
            {/* Template Controls - Icons in button-like containers */}
            {/* <View style={styles.templateControls}>
              <TouchableOpacity
                style={styles.iconButton}
                onPress={handleClickSaveChanges}
                disabled={false} // Allow queueing operations
              >
                <Save color="#000000" />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.iconButton}
                onPress={handleDownloadImage}
                disabled={false} // Allow queueing operations
              >
                <Download color="#000000" />
              </TouchableOpacity>
            </View> */}

            {templatesData.length > 0 ? (
              <TemplateCarousel
                templatesData={templatesData}
                uploadedImages={uploadedImages}
                processedImageUrls={processedImageUrls}
                unpairedBeforeImages={unpairedBeforeImages}
                unpairedAfterImages={unpairedAfterImages}
                beforeImageOptions={beforeImageOptions}
                afterImageOptions={afterImageOptions}
                onPairUnpairedImages={handleManualPair}
                handleSetAfterCroppedImage={updateAfterCroppedImage}
                handleSetBeforeCroppedImage={updateBeforeCroppedImage}
                handleSetBothCroppedImages={updateBothCroppedImages}
                handleSetTemplatesDataTemplate={handleSetTemplatesDataTemplate}
                initialSlideIndex={activeSlideIndex}
                onSlideChange={(index) => {
                  setActiveSlideIndex(index);
                }}
                headerContent={<View style={styles.headerPlaceholder} />}
                floatingSideContent={
                  <View style={styles.headerIcons}>
                    <RainbowBorder
                      isActive={
                        tipsEnabled &&
                        tipsState.saveChanges &&
                        !tipsState.downloadImages
                      }
                      borderRadius={6}
                    >
                      <TouchableOpacity
                        style={styles.iconButton}
                        onPress={() => {
                          handleClickSaveChanges();
                          if (
                            tipsState.saveChanges &&
                            !tipsState.downloadImages
                          ) {
                            markStepComplete(TOOLTIP_STEPS.SAVE_CHANGES);
                          }
                        }}
                      >
                        <Save color="#000000" />
                      </TouchableOpacity>
                    </RainbowBorder>

                    <TouchableOpacity
                      style={styles.iconButton}
                      onPress={handleDownloadImage}
                    >
                      <Download color="#000000" />
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={styles.iconButton}
                      onPress={() => setShowAssignPatientModal(true)}
                    >
                      <PersonAdd size={20} />
                    </TouchableOpacity>
                  </View>
                }
                tipsEnabled={tipsEnabled}
              />
            ) : (
              <View style={styles.emptyTemplatesContainer}>
                <NormalText
                  text="No pairs yet. Use the Unpaired modal to create one."
                  style={styles.emptyTemplatesText}
                />
              </View>
            )}
          </View>

          <View
            style={[
              styles.buttonContainer,
              isSmallScreenDesktop && styles.buttonContainerRow,
            ]}
          >
            <RainbowBorder
              isActive={
                tipsEnabled &&
                tipsState.downloadImages &&
                !tipsState.beforeAfterTipsCompleted
              }
              borderRadius={12}
            >
              <SecondaryButton
                width={180}
                roundBorderRadius
                text="Download All"
                onPress={() => {
                  handleDownloadAllImages();
                  if (
                    tipsState.downloadImages &&
                    !tipsState.beforeAfterTipsCompleted
                  ) {
                    markStepComplete(TOOLTIP_STEPS.BEFORE_AFTER_TIPS_COMPLETED);
                    console.log("Before After onboarding is complete");
                  }
                  console.log("Download all images clicked");
                }}
                disabled={templatesData.length < 1}
                style={
                  isSmallScreenDesktop
                    ? { marginRight: 10 }
                    : { marginBottom: 10 }
                }
              />
            </RainbowBorder>
            <PrimaryButton
              width={180}
              roundBorderRadius
              text="Start Again"
              onPress={() => setShowStartAgainModal(true)}
            />
          </View>
        </View>

        <DrawAllImages
          templatesData={templatesData}
          drawAllImages={drawAllImages}
          setDrawAllImages={setDrawAllImages}
          session={session}
          operationType={currentOperation}
          onImageGenerated={(dataUrl) =>
            handleImageGenerated(dataUrl, activeSlideIndex)
          }
          onRenderError={handleExportRenderError}
          selectedIndex={activeSlideIndex}
          batchUuid={beforeAfterResponse?.batchUuid}
          onManualPairResolved={(templateIndex, concatId) => {
            setTemplatesData((prev) =>
              prev.map((item, i) =>
                i === templateIndex
                  ? {
                      ...item,
                      concat: { ...item.concat, id_in_database: concatId },
                    }
                  : item,
              ),
            );
          }}
        />
      </ScrollView>

      <Modal
        visible={showStartAgainModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowStartAgainModal(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowStartAgainModal(false)}
        >
          <TouchableOpacity
            activeOpacity={1}
            style={styles.modalCard}
            onPress={() => null}
          >
            <TitleText
              text="Start over options"
              fontWeight="600"
              style={styles.modalTitle}
            />
            <NormalText
              text="Start from the beginning with either new uploads or your current images."
              fontWeight="400"
              style={styles.modalBody}
            />
            <View style={styles.modalActions}>
              <SecondaryButton
                text="Start fresh (reset images)"
                width={240}
                roundBorderRadius
                onPress={() => {
                  setShowStartAgainModal(false);
                  onStartAgainFresh();
                }}
              />
              <PrimaryButton
                text="Start over (keep images)"
                width={240}
                roundBorderRadius
                onPress={() => {
                  setShowStartAgainModal(false);
                  onStartAgainWithImages();
                }}
              />
            </View>
            <TouchableOpacity
              onPress={() => setShowStartAgainModal(false)}
              style={styles.modalCancel}
            >
              <NormalText
                text="Cancel"
                fontWeight="500"
                style={styles.modalCancelText}
              />
            </TouchableOpacity>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      <TemplateSettingsModal
        visible={showUnpairedModal}
        onClose={() => setShowUnpairedModal(false)}
      >
        <ManualPairingPanel
          unpairedBeforeImages={unpairedBeforeImages}
          unpairedAfterImages={unpairedAfterImages}
          onPair={handleManualPair}
          disabled={loading}
        />
      </TemplateSettingsModal>

      <AssignImageToPatientModal
        visible={showAssignPatientModal}
        onClose={() => setShowAssignPatientModal(false)}
        onAssign={handleAssignToPatient}
      />

      {tipsEnabled && <StickyOnboardingModal steps={beforeAfterSteps} />}

      {showToast ? (
        <ToastMessage message={toastMessage} style={styles.toast} />
      ) : null}
    </>
  );
};

export default Step4;

const styles = StyleSheet.create({
  container1: {
    flex: 1,
    position: "relative",
  },
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    maxWidth: 1000,
    marginHorizontal: "auto",
    gap: 15,
  },
  carouselWrapper: {
    position: "relative",
    width: "100%",
  },
  emptyTemplatesContainer: {
    width: "100%",
    minHeight: 220,
    borderWidth: 1,
    borderColor: "#DDDEE0",
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
  emptyTemplatesText: {
    color: "#4B5563",
    fontSize: 14,
    textAlign: "center",
    paddingHorizontal: 12,
  },
  templateControls: {
    position: "absolute",
    top: 8,
    right: 0,
    zIndex: 10,
    gap: 8,
    flexDirection: "column",
  },
  headerPlaceholder: {
    width: 60,
    height: 1,
  },
  headerIcons: {
    flexDirection: "column",
    gap: 8,
  },
  iconButton: {
    backgroundColor: "#F8F8F8",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    padding: 1,
    alignItems: "center",
    justifyContent: "center",
    width: 30,
    height: 30,
  },
  buttonContainer: {
    marginTop: "auto",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  noAutoPairAlert: {
    width: "100%",
    maxWidth: 860,
    borderWidth: 1,
    borderColor: "#F59E0B",
    backgroundColor: "#FFFBEB",
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    gap: 10,
    alignItems: "center",
  },
  noAutoPairAlertText: {
    color: "#92400E",
    textAlign: "center",
    fontSize: 14,
  },
  toast: {
    position: "absolute",
    top: -30,
    right: 20,
    zIndex: 9999,
  },
  buttonContainerRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalCard: {
    width: "100%",
    maxWidth: 420,
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 20,
    alignItems: "center",
  },
  modalTitle: {
    fontSize: 20,
    color: "#111827",
    lineHeight: 20 * 1.4,
    letterSpacing: 0.2,
    fontStyle: "normal",
    marginBottom: 8,
    textAlign: "center",
  },
  modalBody: {
    fontSize: 14,
    color: "#4B5563",
    marginBottom: 16,
    textAlign: "center",
  },
  modalActions: {
    gap: 10,
    width: "100%",
    alignItems: "center",
  },
  modalCancel: {
    marginTop: 12,
    alignItems: "center",
  },
  modalCancelText: {
    color: "#6B7280",
    fontSize: 14,
  },
});
